import { useState, useEffect } from "react";
import { Printer, Wifi, WifiOff, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { checkPrinterReachable, usePrintLabel } from "@/hooks/usePrintLabel";
import { saveLastPrintQty } from "@/hooks/usePrinterStatus";
import { recordPrint, useInvalidatePrintLog } from "@/hooks/usePrintLog";
import type { CaseLabelFields, CaseLabelFormatting, LabelFieldKey, LogoConfig } from "@/hooks/useCaseLabels";
import { LABEL_FIELD_DEFS, fieldDisplayValue } from "@/hooks/useCaseLabels";

interface PrintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fields: CaseLabelFields;
  formatting: CaseLabelFormatting;
  size: "4x4" | "2x4";
  initialQuantity?: number;
  labelId?: string | null;
  labelName?: string;
  logo?: LogoConfig | null;
}

export function PrintDialog({ open, onOpenChange, fields, formatting, size, initialQuantity, labelId, labelName, logo }: PrintDialogProps) {
  const [reachable, setReachable] = useState<boolean | null>(null);
  const [quantity, setQuantity] = useState(initialQuantity ?? 1);
  const [quantityInput, setQuantityInput] = useState(String(initialQuantity ?? 1));
  const [quantityTouched, setQuantityTouched] = useState(false);
  const printLabel = usePrintLabel();
  const invalidatePrintLog = useInvalidatePrintLog();

  // Sync quantity when initialQuantity changes (e.g. "print X more" button)
  useEffect(() => {
    if (initialQuantity) {
      setQuantity(initialQuantity);
      setQuantityInput(String(initialQuantity));
    }
  }, [initialQuantity]);

  // Probe the printer every time the dialog opens
  useEffect(() => {
    if (!open) return;
    setReachable(null);
    setQuantityTouched(false);
    checkPrinterReachable().then(setReachable);
  }, [open]);

  async function handlePrint() {
    // Apply field prefixes so the printer receives the full display string
    const printFields: CaseLabelFields = {};
    for (const def of LABEL_FIELD_DEFS) {
      const v = fields[def.key as LabelFieldKey];
      if (v) printFields[def.key as LabelFieldKey] = fieldDisplayValue(def.key as LabelFieldKey, v);
    }
    await printLabel.mutateAsync({ fields: printFields, formatting, size, quantity, logo });
    saveLastPrintQty(quantity);
    // Record in audit log (fire and forget — don't block UI)
    void recordPrint({
      label_id:   labelId,
      label_name: labelName ?? fields.lot_id ?? "Untitled",
      size,
      quantity,
    }).then(() => invalidatePrintLog(labelId));
    onOpenChange(false);
  }

  const checking      = reachable === null;
  const parsedQuantity = quantityInput === "" ? NaN : Number(quantityInput);
  const quantityValid = Number.isInteger(parsedQuantity) && parsedQuantity >= 1 && parsedQuantity <= 999;
  const canPrint      = reachable === true && !printLabel.isPending && quantityValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-4 w-4" />
            Print Labels
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Printer status */}
          <div className="flex items-center gap-2.5">
            {checking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Checking printer…</span>
              </>
            ) : reachable ? (
              <>
                <Wifi className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-600 dark:text-green-400">
                  Printer reachable at 192.168.1.77
                </span>
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">
                  Printer not found — check network connection
                </span>
              </>
            )}
          </div>

          {/* Label size (informational) */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Label size</span>
            <Badge variant="outline" className="font-mono text-xs">{size}</Badge>
          </div>

          {/* Quantity */}
          <div className="space-y-1.5">
            <Label htmlFor="print-qty" className="text-sm">Quantity</Label>
            <div className="flex items-center gap-2">
              <Input
                id="print-qty"
                type="number"
                min={1}
                max={999}
                value={quantityInput}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!/^\d*$/.test(raw)) return;
                  setQuantityInput(raw);
                  if (raw !== "") setQuantity(Math.max(1, Math.min(999, Number(raw))));
                }}
                onBlur={() => setQuantityTouched(true)}
                onKeyDown={(e) => { if (e.key === "Enter" && canPrint) handlePrint(); }}
                onFocus={(e) => e.target.select()}
                autoFocus
                aria-invalid={quantityTouched && !quantityValid}
                className="w-24 h-8 text-sm"
              />
              <span className="text-sm text-muted-foreground">
                label{quantity !== 1 ? "s" : ""}
              </span>
            </div>
            {quantityTouched && !quantityValid && (
              <p className="text-xs text-destructive">Please enter a number between 1 and 999.</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={printLabel.isPending}>
            Cancel
          </Button>
          <Button onClick={handlePrint} disabled={!canPrint}>
            {printLabel.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Printing…
              </>
            ) : (
              <>
                <Printer className="h-3.5 w-3.5 mr-1.5" />
                Print {quantity} label{quantity !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
